"use client";

import { useState } from "react";
import Link from "next/link";
import { toast } from "sonner";
import { Coins, Info, Users } from "lucide-react";
import { Button, type buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/store/store";
import { useGetCreditBalanceQuery } from "@/store/api/creditApi";
import { useCreditGuard } from "@/hooks/useCreditGuard";
import type { BillingSettings } from "@/store/api/pricingApi";
import { formatCredits } from "@/lib/utils/credit.utils";
import { formatMoney } from "@/lib/utils/format.utils";
import { cn } from "@/lib/utils/utils";
import type { VariantProps } from "class-variance-authority";

/**
 * The stops on the slider, as multiples of the published bundle.
 *
 * Uneven on purpose, and dense at the cheap end — the difference between one bundle and two matters to
 * somebody topping up, while the difference between eight and nine does not.
 *
 * MULTIPLES rather than absolute credit counts. Deriving them means the slider tracks
 * whatever rate the credit service publishes instead of drifting the day that
 * rate changes.
 *
 * The 10x ceiling is deliberately the one the inline calculator already shipped
 * with. Raising it is a commercial decision, not a UI one.
 */
const STEP_MULTIPLIERS = [1, 2, 3, 4, 5, 6, 8, 10];

/** The sentinel an account carries when it has never been upgraded. */
const FREE_PLAN = "free forever";

interface BuyCreditsDialogProps {
  billingSettings: BillingSettings | null | undefined;
  /** Trigger styling, so the same dialog can be a tile button or a page CTA. */
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  label?: string;
  className?: string;
  /**
   * Controlled mode, for opening this from something that is not its own
   * button — the credit guard hands over to it when an action is blocked for
   * want of credits, so somebody who ran out mid-search tops up without
   * leaving the page they were on. Passing `open` suppresses the trigger
   * entirely; leave both out and the dialog owns its own state as before.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Pay-as-you-go top-up, as a modal.
 *
 * This was an inline card at the foot of the Pricing page. It is a dialog now
 * because Billing needs the same flow, and two copies of a priced slider is one
 * copy too many — the price a customer is quoted must not depend on which
 * screen they happened to open.
 *
 * The same bands in the same order,
 * the same index-mapped snap slider with tick marks and the value floating
 * under the handle. This multiplies the published rate linearly, which
 * is less flattering and actually true.
 *
 * Positions are index-mapped, not value-mapped: the stops are unevenly spaced
 * in credits but evenly spaced on the track, so every stop is the same size to
 * hit. A value-mapped slider would bunch the first four stops into a tenth of
 * the track.
 *
 * The balance comes from this component's own query rather than a prop. It is
 * the same cached request the top bar already makes, so asking here costs
 * nothing and saves both callers from threading an account through.
 */
export function BuyCreditsDialog({
  billingSettings,
  variant = "default",
  size = "default",
  label = "Buy credits",
  className,
  open: controlledOpen,
  onOpenChange,
}: BuyCreditsDialogProps) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const planName = useAppSelector((state) => state.auth.currentUser?.planName);
  // Credits are shared from the team's pool - a member can SEE this dialog
  // (the trigger stays enabled so Billing reads the same for everyone) but
  // can't actually spend anything from it. Mirrors CreditGuardModal's own
  // team-aware block, just reached from a different door.
  const { isTeamMember } = useCreditGuard();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  // The INDEX into STEP_MULTIPLIERS, which is also the slider's own value.
  const [stepIndex, setStepIndex] = useState(0);

  const { data: account, isLoading: isBalanceLoading } = useGetCreditBalanceQuery(
    undefined,
    // Only asked once somebody opens the dialog.
    { skip: !isAuthenticated || !open }
  );

  const lastIndex = STEP_MULTIPLIERS.length - 1;
  const multiplier = STEP_MULTIPLIERS[stepIndex] ?? 1;

  const bundleCredits = billingSettings?.topUpCredits ?? 0;
  const bundlePrice = billingSettings?.topUpPrice ?? 0;
  const currency = billingSettings?.currency ?? "USD";

  const credits = bundleCredits * multiplier;
  const price = bundlePrice * multiplier;
  const balance = account?.runningBalance ?? 0;

  // A never-upgraded account cannot buy add-on credits, so the dialog becomes an
  // explanation and the primary action becomes the upgrade path.
  const isFreePlan = (planName || "").toLowerCase().includes(FREE_PLAN);

  // Hidden at the ends, where it would collide with the min/max labels.
  const isAtEnd = stepIndex === 0 || stepIndex === lastIndex;
  const handlePercent = (stepIndex / lastIndex) * 100;

  // Reseeded on open rather than in an effect, so a cancelled adjustment is
  // genuinely discarded and the reset runs once per open, not per render.
  const handleOpenChange = (next: boolean) => {
    if (next) setStepIndex(0);
    if (isControlled) onOpenChange?.(next);
    else setUncontrolledOpen(next);
  };

  const handleBuy = () => {
    toast.success(`Thanks for your interest in ${formatCredits(credits)} credits`, {
      description: "Our team will reach out shortly to arrange payment.",
    });
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* A controlled caller opens this itself and has its own button. */}
      {isControlled ? null : (
        <DialogTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={className}
            disabled={!billingSettings}
          >
            {label}
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Need more credits?</DialogTitle>
          <DialogDescription>
            {isTeamMember
              ? "Your credits are shared from your team's pool."
              : "Buy additional credits anytime to keep powering your workflows."}
          </DialogDescription>
        </DialogHeader>

        {/* Where the customer stands today, before they change anything. */}
        <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
          <div>
            <p className="text-sm text-muted-foreground">Available credits</p>
            {isBalanceLoading ? (
              <Skeleton className="mt-1.5 h-8 w-28" />
            ) : (
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                {formatCredits(balance)}
              </p>
            )}
          </div>

          {/*
            There is no knowledge-base page in this app and no KB URL
            anywhere in the repo, so this shows the published rate instead.
          */}
          {billingSettings && !isTeamMember ? (
            <p className="text-sm text-muted-foreground tabular-nums">
              {formatMoney(bundlePrice, currency)} per{" "}
              {formatCredits(bundleCredits)} credits
            </p>
          ) : null}
        </div>

        {/*
          A team member draws from the pool their admin funds - they can see
          the balance above, but the slider/buy flow below is not theirs to
          use. Mirrors CreditGuardModal's own team-aware block (ask your
          admin, no purchase button), just reached from this door instead of
          an insufficient-credits check.
        */}
        {isTeamMember ? (
          <div className="flex items-start gap-2 rounded-lg border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-muted-foreground">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand/70" />
            <p>
              Ask your team admin to add more credits to the pool — they can
              do this from their own Billing page.
            </p>
          </div>
        ) : (
        <div className="space-y-6 py-1">
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">
              Refill your credit balance
            </p>

            <div className="relative pt-6">
              {/* The selected amount, tracking the handle. */}
              {isAtEnd ? null : (
                <div
                  className="absolute top-0 -translate-x-1/2"
                  style={{ left: `${handlePercent}%` }}
                >
                  <span className="whitespace-nowrap text-sm font-bold text-primary tabular-nums">
                    {formatCredits(credits)}
                  </span>
                </div>
              )}

              <div className="relative">
                <Slider
                  min={0}
                  max={lastIndex}
                  step={1}
                  value={[stepIndex]}
                  onValueChange={(value) => setStepIndex(value[0])}
                  className="cursor-pointer"
                  aria-label="Credits to purchase"
                  aria-valuetext={`${formatCredits(credits)} credits`}
                />

                {/*
                  One tick per stop, so the track reads as a set of choices
                  rather than a continuous sweep.
                */}
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 h-1.5"
                  aria-hidden="true"
                >
                  {STEP_MULTIPLIERS.map((step, index) => (
                    <span
                      key={`tick-${step}`}
                      className="absolute top-0 h-1.5 w-px -translate-x-1/2 bg-border"
                      style={{ left: `${(index / lastIndex) * 100}%` }}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between text-sm text-muted-foreground tabular-nums">
                <span>{formatCredits(bundleCredits)}</span>
                <span>
                  {formatCredits(bundleCredits * STEP_MULTIPLIERS[lastIndex])}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4 rounded-lg bg-muted/40 px-4 py-3">
            <div>
              <p className="text-sm text-muted-foreground">You will pay</p>
              <p className="mt-1 flex items-baseline gap-2 text-2xl font-bold tabular-nums text-foreground">
                {formatMoney(price, currency)}
                <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                  <Coins className="h-4 w-4 text-brand/70" />
                  {formatCredits(credits)} credits
                </span>
              </p>
            </div>

            {/* It is the
                question a customer is actually answering. */}
            <p className="text-xs text-muted-foreground tabular-nums">
              Balance after:{" "}
              <span className="font-medium text-foreground">
                {formatCredits(balance + credits)}
              </span>
            </p>
          </div>

          {isFreePlan ? (
            <div className="flex items-start gap-2 rounded-lg border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand/70" />
              <p>
                <span className="font-semibold text-foreground">Note:</span> Add-on
                credits are available on a paid plan. Upgrade first and you can top
                up any time.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Credits are arranged with our team — nothing is charged from this
              screen.
            </p>
          )}
        </div>
        )}

        <DialogFooter className="gap-3 sm:gap-3">
          {isTeamMember ? (
            <Button
              className="flex-1 font-semibold"
              onClick={() => handleOpenChange(false)}
            >
              Got it
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>

              {isFreePlan ? (
                <Button className="flex-1 font-semibold" asChild>
                  <Link href="/settings/pricing" onClick={() => handleOpenChange(false)}>
                    Upgrade your plan
                  </Link>
                </Button>
              ) : (
                <Button
                  className={cn("flex-1 font-semibold")}
                  onClick={handleBuy}
                  disabled={!billingSettings}
                >
                  Buy {formatCredits(credits)} credits
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BuyCreditsDialog;
